import { useState, useEffect } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  effectiveType: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown';
  downlink: number; // Mbps
  rtt: number; // Round trip time in ms
  saveData: boolean; // User has enabled data saver mode
  isSlowConnection: boolean; // Derived: true if 2g or slow-2g
}

// Extend Navigator interface for Network Information API
interface NavigatorWithConnection extends Navigator {
  connection?: {
    effectiveType: '2g' | '3g' | '4g' | 'slow-2g';
    downlink: number;
    rtt: number;
    saveData: boolean;
    addEventListener: (type: string, listener: EventListener) => void;
    removeEventListener: (type: string, listener: EventListener) => void;
  };
}

export function useNetworkStatus(): NetworkStatus {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(() => {
    if (typeof window === 'undefined') {
      return {
        isOnline: true,
        effectiveType: 'unknown',
        downlink: 10,
        rtt: 50,
        saveData: false,
        isSlowConnection: false
      };
    }

    const nav = navigator as NavigatorWithConnection;
    const connection = nav.connection;

    return {
      isOnline: navigator.onLine,
      effectiveType: connection?.effectiveType || 'unknown',
      downlink: connection?.downlink || 10,
      rtt: connection?.rtt || 50,
      saveData: connection?.saveData || false,
      isSlowConnection: connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g' || false
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateOnlineStatus = () => {
      setNetworkStatus(prev => ({
        ...prev,
        isOnline: navigator.onLine
      }));
    };

    const updateConnectionStatus = () => {
      const nav = navigator as NavigatorWithConnection;
      const connection = nav.connection;

      if (connection) {
        setNetworkStatus(prev => ({
          ...prev,
          effectiveType: connection.effectiveType || prev.effectiveType,
          downlink: connection.downlink || prev.downlink,
          rtt: connection.rtt || prev.rtt,
          saveData: connection.saveData || prev.saveData,
          isSlowConnection: connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g'
        }));
      }
    };

    // Listen for online/offline events
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    // Listen for connection changes (Network Information API)
    const nav = navigator as NavigatorWithConnection;
    if (nav.connection) {
      nav.connection.addEventListener('change', updateConnectionStatus);
    }

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);

      if (nav.connection) {
        nav.connection.removeEventListener('change', updateConnectionStatus);
      }
    };
  }, []);

  return networkStatus;
}
